import { useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { usePwaUpdate } from './PwaUpdateContext';

// 15 minutes in production, 2 minutes in development
const intervalMS = import.meta.env.PROD ? 15 * 60 * 1000 : 2 * 60 * 1000;

const MAX_AUTO_RELOADS_PER_SESSION = 2;
const AUTO_RELOAD_KEY = 'pwa:autoReloads';

function canAutoReload() {
  const n = Number(sessionStorage.getItem(AUTO_RELOAD_KEY) ?? '0');
  return n < MAX_AUTO_RELOADS_PER_SESSION;
}

function bumpAutoReloadCounter() {
  const n = Number(sessionStorage.getItem(AUTO_RELOAD_KEY) ?? '0');
  const next = n + 1;
  sessionStorage.setItem(AUTO_RELOAD_KEY, String(next));
  return next;
}

export function PwaUpdater() {
  const {
    setNeedRefresh,
    setOfflineReady,
    setRegistration,
    setUpdateServiceWorker,
  } = usePwaUpdate();

  const intervalRef = useRef<number | null>(null);
  const onVisRef = useRef<(() => void) | null>(null);
  const warmupTimerRef = useRef<number | null>(null);
  const didWarmupRef = useRef(false);

  const { updateServiceWorker } = useRegisterSW({
    immediate: true,

    onRegisteredSW(_swUrl, r) {
      setRegistration(r ?? null);

      if (intervalRef.current) clearInterval(intervalRef.current);
      if (r) {
        intervalRef.current = window.setInterval(() => {
          r.update().catch((err) =>
            console.warn('SW update check failed:', err),
          );
        }, intervalMS);
      }

      const onVis = () => {
        if (!document.hidden && r) {
          r.update().catch((err) =>
            console.warn('SW update on visibility failed:', err),
          );
        }
      };

      if (onVisRef.current) {
        document.removeEventListener('visibilitychange', onVisRef.current);
      }
      onVisRef.current = onVis;
      document.addEventListener('visibilitychange', onVis);

      // warm-up
      if (r && !didWarmupRef.current) {
        didWarmupRef.current = true;

        if (warmupTimerRef.current) clearTimeout(warmupTimerRef.current);
        warmupTimerRef.current = window.setTimeout(() => {
          r.update().catch((err) =>
            console.warn('SW warm-up update check failed:', err),
          );
        }, 1000);

        const onLoad = () => {
          r.update().catch((err) =>
            console.warn('SW post-load update check failed:', err),
          );
        };

        if (document.readyState === 'complete') onLoad();
        else window.addEventListener('load', onLoad, { once: true });
      }
    },

    onNeedRefresh() {
      setNeedRefresh(true);

      if (!canAutoReload()) return;
      bumpAutoReloadCounter();

      Promise.resolve(updateServiceWorker(true)).catch((err) => {
        console.warn('Auto-apply SW update failed:', err);
      });
    },

    onOfflineReady() {
      setOfflineReady(true);
    },
  });

  // IMPORTANT:
  // Here we pass the function itself (not () => updateServiceWorker)
  useEffect(() => {
    setUpdateServiceWorker(updateServiceWorker);
    return () => setUpdateServiceWorker(null);
  }, [setUpdateServiceWorker, updateServiceWorker]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);

      if (onVisRef.current) {
        document.removeEventListener('visibilitychange', onVisRef.current);
      }

      if (warmupTimerRef.current) clearTimeout(warmupTimerRef.current);
    };
  }, []);

  return null;
}
